import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Divider,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { styled } from '@mui/material/styles';
import { getCompanyInfo } from '../../../services/migration/companyDataService';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { accountingApi } from '../../../config/apiClient';
import { ACCOUNTING_API } from '../../../config/api';
import {
  OfferTemplateConfig,
  DEFAULT_TEMPLATE_CONFIG,
  mapTemplateFromApi,
} from '../../../types/offerTemplate.types';
import OfferPdfTemplate, {
  BranchInfoData as PdfBranchInfoData,
} from './OfferPdfTemplate';

// Teklif Kalemi arayüzü
interface OfferItem {
  id: string;
  name: string;
  itemName?: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  vatRate: number;
  sctRate?: number;
  totalPrice: number;
  grossTotal?: number; // Tüm vergiler dahil toplam tutar
}

// Şirket/Şube bilgileri arayüzü
interface BranchInfoData {
  name: string;
  taxNumber?: string;
  taxOffice?: string;
  address?: string;
  phone?: string;
  email?: string;
  mersisNo?: string;
  tradeRegistryNo?: string;
  bankAccounts?: Array<{bankName: string; iban: string;}>;
}

// Verilen Teklif arayüzü
interface GivenOfferData {
  id: string;
  offerNumber: string;
  accountId: string;
  account: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  subtotal: number;
  vatTotal: number;
  sctTotal: number;
  discountTotal: number;
  additionalDiscountAmount?: number;
  discountType?: 'percentage' | 'amount' | null;
  discountValue?: number | null;
  grandTotal: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  currency: string;
  createdAt: string;
  updatedAt?: string;
  items?: OfferItem[];
  notes?: string;
  branchInfo?: BranchInfoData;
}

// Alınan Teklif arayüzü
interface ReceivedOfferData {
  id: string;
  offerNumber: string;
  supplierId: string;
  supplier: {
    companyName: string;
    taxNumber?: string;
    taxOffice?: string;
    address?: string;
    phone?: string;
    email?: string;
  };
  date: string;
  validUntil: string;
  amount: number;
  status: 'draft' | 'pending' | 'approved' | 'rejected';
  currency: string;
  createdAt: string;
  updatedAt?: string;
  items?: OfferItem[];
  notes?: string;
  branchInfo?: BranchInfoData;
}

// Birleştirilmiş Teklif tipi
type OfferData = GivenOfferData | ReceivedOfferData;

interface OfferDisplayModalProps {
  open: boolean;
  onClose: () => void;
  offer: OfferData | null;
  offerType: 'given' | 'received';
  companyId: string | null;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '6px 8px',
  fontSize: '0.8rem',
}));

const StyledTableHeadCell = styled(StyledTableCell)(({ theme }) => ({
  fontWeight: 'bold',
  backgroundColor: theme.palette.grey[200],
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  fontSize: '1rem',
  color: theme.palette.primary.main,
}));

const formatCurrency = (amount: number | undefined, currencyCode: string = 'TRY'): string => {
  if (typeof amount !== 'number') return '-';
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
    if (dateString.includes('T') && dateString.includes('Z')) {
      // "2025-06-03T06:26:58.262Z" formatını parçalara ayır
      const [tarihKismi, saatKismi] = dateString.split('T');
      
      // Tarih kısmını Türkçe formatına çevir (yyyy-MM-dd -> dd.MM.yyyy)
      const [yil, ay, gun] = tarihKismi.split('-');
      const formatliTarih = `${gun}.${ay}.${yil}`;
      
      return formatliTarih;
    }
    
    // ISO formatında değilse normal tarih dönüşümü yap
    const date = new Date(dateString);
    // Tarih geçerli mi kontrolü
    if (isNaN(date.getTime())) {
        return dateString; // Geçersizse orijinal dizeyi döndür
    }
    
    // Saat bilgisini 00:00:00 olarak sıfırla
    // Bu saat bilgisi çevirisi nedeniyle oluşabilecek 
    // UTC veya zaman dilimi farklılıklarını önler
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
    
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(localDate);
  } catch (e) {
    console.error("Tarih formatı hatası:", e);
    return dateString; // Hata durumunda orijinal dizeyi döndür
  }
};

const formatDateTime = (dateTimeString: string | undefined): string => {
  if (!dateTimeString) return '-';
  try {
    // ISO formatında bir tarih mi kontrol et (T ve Z içeriyor mu?)
    if (dateTimeString.includes('T') && dateTimeString.includes('Z')) {
      // "2025-06-03T06:26:58.262Z" formatını parçalara ayır
      const [_, saatKismi] = dateTimeString.split('T');
      
      // Saat kısmını milisaniye olmadan al (06:26:58.262Z -> 06:26:58)
      const formatliSaat = saatKismi.split('.')[0];
      
      return formatliSaat;
    }
    
    const date = new Date(dateTimeString);
    // Geçerli bir tarih mi kontrol et
    if (isNaN(date.getTime())) {
      return '-';
    }
    
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  } catch (e) {
    console.error("Saat formatı hatası:", e);
    return '-';
  }
};

// Yazdırma stilleri
const printStyles = `
  @media print {
    body > *:not(.MuiDialog-root),
    .MuiDialog-root > *:not(.MuiDialog-container),
    .MuiDialog-container > *:not(.MuiPaper-root),
    .MuiDialog-paper > *:not(.MuiDialogContent-root),
    .MuiDialogTitle-root,
    .MuiDialogActions-root {
      display: none !important;
    }

    .MuiDialog-root,
    .MuiDialog-container,
    .MuiDialog-paper,
    .MuiDialogContent-root {
      display: block !important;
      width: auto !important;
      height: auto !important;
      overflow: visible !important;
      background: transparent !important;
      box-shadow: none !important;
      padding: 0 !important;
      margin: 0 !important;
    }

    #printableOfferContent {
      width: 210mm !important; 
      min-height: 290mm !important;
      padding: 10mm !important;
      margin: 0 auto !important;
      box-shadow: none !important;
      border: none !important;
      background-color: white !important;
      page-break-before: auto !important;
      page-break-after: auto !important;
      font-size: 10pt;
    }

    @page {
      size: A4;
      margin: 0;
    }

    #printableOfferContent .MuiPaper-root {
      box-shadow: none !important;
      border: 1px solid #ccc !important;
      background-color: white !important;
    }

    #printableOfferContent table {
      width: 100% !important;
      page-break-inside: auto;
    }
    #printableOfferContent tr {
      page-break-inside: avoid;
      page-break-after: auto;
    }
    #printableOfferContent thead {
      display: table-header-group;
    }
  }
`;

// Teklif durumu metin karşılıkları
const getStatusText = (status: string): string => {
  switch (status) {
    case 'approved':
      return 'Onaylandı';
    case 'pending':
      return 'Beklemede';
    case 'rejected':
      return 'Reddedildi';
    case 'draft':
      return 'Taslak';
    default:
      return status;
  }
};

const OfferDisplayModal: React.FC<OfferDisplayModalProps> = ({
  open,
  onClose,
  offer,
  offerType,
  companyId,
}) => {
  const pdfTemplateRef = useRef<HTMLDivElement>(null);
  const [fetchedBranchInfo, setFetchedBranchInfo] = useState<BranchInfoData | null>(null);
  const [loadingBranchInfo, setLoadingBranchInfo] = useState<boolean>(false);
  const [branchInfoError, setBranchInfoError] = useState<string | null>(null);

  const [templateConfig, setTemplateConfig] = useState<OfferTemplateConfig>(DEFAULT_TEMPLATE_CONFIG);
  const [templateList, setTemplateList] = useState<OfferTemplateConfig[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Firma bilgilerini yükleme
  useEffect(() => {
    if (open && companyId) {
      setLoadingBranchInfo(true);
      setBranchInfoError(null);
      setFetchedBranchInfo(null);
      
      getCompanyInfo(companyId)
        .then((info) => {
          if (info) {
            setFetchedBranchInfo(info as BranchInfoData);
          } else {
            setBranchInfoError("Firma bilgileri bulunamadı.");
            setFetchedBranchInfo({ name: 'Firma Bilgileri Yok' });
          }
        })
        .catch((err) => {
          console.error("Firma bilgileri yüklenirken hata:", err);
          setBranchInfoError("Firma bilgileri yüklenirken bir hata oluştu.");
          setFetchedBranchInfo({ name: 'Hata oluştu' });
        })
        .finally(() => {
          setLoadingBranchInfo(false);
        });
    } else {
        setFetchedBranchInfo(null);
        setLoadingBranchInfo(false);
        setBranchInfoError(null);
    }
  }, [open, companyId]);

  // Şablon konfigürasyonlarını yükleme
  useEffect(() => {
    if (!open) return;
    const loadTemplates = async () => {
      try {
        setLoadingTemplate(true);
        const [defaultRes, listRes] = await Promise.all([
          accountingApi.get(ACCOUNTING_API.OFFER_TEMPLATE_DEFAULT),
          accountingApi.get(ACCOUNTING_API.OFFER_TEMPLATE_LIST),
        ]);

        if (listRes.data?.success && Array.isArray(listRes.data.data)) {
          const mapped = listRes.data.data.map(mapTemplateFromApi);
          setTemplateList(mapped);
        }

        if (defaultRes.data?.success && defaultRes.data.data) {
          const defaultTpl = mapTemplateFromApi(defaultRes.data.data);
          setTemplateConfig(defaultTpl);
          setSelectedTemplateId(defaultTpl.id);
        } else {
          setTemplateConfig(DEFAULT_TEMPLATE_CONFIG);
          setSelectedTemplateId('');
        }
      } catch (err) {
        console.error('Şablon yüklenirken hata (varsayılan şablon kullanılacak):', err);
        setTemplateConfig(DEFAULT_TEMPLATE_CONFIG);
        setTemplateList([]);
        setSelectedTemplateId('');
      } finally {
        setLoadingTemplate(false);
      }
    };
    loadTemplates();
  }, [open]);

  const handleTemplateChange = async (templateId: string) => {
    const cached = templateList.find(t => t.id === templateId);
    if (cached) {
      setSelectedTemplateId(templateId);
      setTemplateConfig(cached);
      return;
    }
    try {
      const res = await accountingApi.get(ACCOUNTING_API.OFFER_TEMPLATE_BY_ID(templateId));
      if (res.data?.success && res.data.data) {
        const tpl = mapTemplateFromApi(res.data.data);
        setSelectedTemplateId(templateId);
        setTemplateConfig(tpl);
      }
    } catch (err) {
      console.error('Şablon yüklenirken hata:', err);
    }
  };
  
  // Teklif yoksa hiçbir şey gösterme
  if (!offer) {
    return null;
  }

  // Teklif tipini kontrol et
  const isGivenOffer = offerType === 'given';
  
  // Log amaçlı
  console.log("Teklif Tipi:", offerType);
  console.log("Teklif Verisi:", offer);
  console.log("CompanyId:", companyId);
  
  // Teklif başlığını belirle
  const getOfferTitle = (): string => {
    return isGivenOffer ? 'VERİLEN TEKLİF' : 'ALINAN TEKLİF';
  };

  // Teklif kalemlerini hazırla
  const items = offer.items || [];
  console.log("Teklif Kalemleri:", items);
  
  // Cari bilgilerini hazırla - doğrudan teklif içinden al
  const accountInfo = isGivenOffer 
    ? (offer as GivenOfferData).account
    : (offer as ReceivedOfferData).supplier;
  
  console.log("Cari Bilgileri:", accountInfo);

  // Toplam tutar
  const totalAmount = isGivenOffer 
    ? (offer as GivenOfferData).grandTotal
    : (offer as ReceivedOfferData).amount;

  // html2canvas metni canvas'a kendisi yeniden cizer; Safari'de kerning ve
  // ligatur hesabi sayfa duzenindeki olcumle birebir uyusmayabiliyor ve
  // harfler ust uste biniyor (cihaza/Safari surumune gore degisen bir bug).
  // Capture edilen klonda kerning+ligaturleri kapatarak olcum ile cizimi
  // esitliyoruz; ekrandaki onizleme etkilenmez.
  const captureElement = (el: HTMLElement) =>
    html2canvas(el, {
      scale: 2,
      logging: false,
      useCORS: true,
      allowTaint: true,
      onclone: (clonedDoc) => {
        const style = clonedDoc.createElement('style');
        style.textContent =
          '* { font-kerning: none !important; font-variant-ligatures: none !important; letter-spacing: normal !important; }';
        clonedDoc.head.appendChild(style);
      },
    });

  const handleDownloadPdf = async () => {
    const container = pdfTemplateRef.current || document.getElementById('printableOfferContent');
    if (!container) return;

    // Webfont henuz yuklenmeden capture edilirse html2canvas fallback font
    // metrikleriyle cizer ve yazi kayar — once font yuklemesini bekle.
    try {
      await document.fonts.ready;
    } catch {
      // FontFaceSet desteklenmiyorsa mevcut davranisla devam et.
    }

    const pageElements = container.querySelectorAll('[data-pdf-page]');
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    if (pageElements.length > 0) {
      for (let i = 0; i < pageElements.length; i++) {
        const el = pageElements[i] as HTMLElement;
        const canvas = await captureElement(el);
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgProps = pdf.getImageProperties(imgData);
        const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
        if (i > 0) pdf.addPage();
        if (imgHeight <= pdfHeight + 1) {
          pdf.addImage(imgData, 'JPEG', 0, 0, pdfWidth, imgHeight);
        } else if (imgHeight <= pdfHeight * 1.05) {
          // Küçük taşmalarda içeriği ortadan kesmek yerine sayfaya sığacak şekilde hafifçe küçült
          const scale = pdfHeight / imgHeight;
          const scaledWidth = pdfWidth * scale;
          pdf.addImage(imgData, 'JPEG', (pdfWidth - scaledWidth) / 2, 0, scaledWidth, pdfHeight);
        } else {
          let yOffset = 0;
          let pageIdx = 0;
          while (yOffset < imgHeight) {
            if (pageIdx > 0) pdf.addPage();
            pdf.addImage(imgData, 'JPEG', 0, -yOffset, pdfWidth, imgHeight);
            yOffset += pdfHeight;
            pageIdx++;
          }
        }
      }
    } else {
      const canvas = await captureElement(container as HTMLElement);
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;
      let yOffset = 0;
      while (yOffset < imgHeight) {
        if (yOffset > 0) pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, -yOffset, pdfWidth, imgHeight);
        yOffset += pdfHeight;
      }
    }
    pdf.save(`Teklif_${offer.offerNumber}.pdf`);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <GlobalStyles styles={printStyles} />
      <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white', p: 1.5, px:2.5 }}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {getOfferTitle()}
          </Typography>
          {templateList.length > 0 && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={selectedTemplateId}
                onChange={(e) => handleTemplateChange(e.target.value)}
                displayEmpty
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', '.MuiSelect-icon': { color: '#fff' }, fontSize: '0.85rem' }}
              >
                {templateList.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.templateName} {t.isDefault ? '⭐' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Grid>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, backgroundColor: '#e8e8e8' }}>
        {(loadingBranchInfo || loadingTemplate) ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ p: '20px', m: 'auto', display: 'flex', justifyContent: 'center' }}>
            <Box id="printableOfferContent" sx={{ boxShadow: '0 0 10px rgba(0,0,0,0.1)' }}>
              <OfferPdfTemplate
                ref={pdfTemplateRef}
                config={templateConfig}
                companyInfo={fetchedBranchInfo as PdfBranchInfoData}
                offer={offer as any}
                offerType={offerType}
              />
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 1.5, backgroundColor: 'grey.100', borderTop: '1px solid #ddd' }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Kapat
        </Button>
        <Button 
          onClick={handleDownloadPdf} 
          variant="outlined" 
          color="primary"
          sx={{ mr: 1 }}
        >
          PDF İndir
        </Button>
        <Button 
          onClick={() => window.print()} 
          variant="contained" 
          color="secondary"
        >
          Yazdır
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OfferDisplayModal;
export type { GivenOfferData, ReceivedOfferData }; 